import React from "react";
import axios from "axios";
import { Link, Switch, Route, Redirect } from "react-router-dom";
import ReactPaginate from "react-paginate";

import SubHeader from "../../components/common/topHeader/subHeader";
import ModalManager from "../../components/common/modal/ModalManager";
import TextInput from "../../components/common/inputs/textInput";
import NotificationManager from "../../utils/notificationManager";
import Loading from "../../components/common/loading";
import LoggingUserLogs from "../logging/userLogs";
import OutletCard from "../../components/devices/OutletCard";

import "./deviceGroups.css";
import "./deviceGroupSettings.css";

const DEFAULT_PAGE_SIZE = 15;

class OutletGroupOutlets extends React.Component {
  state = {
    outlets: [],
    loading: false,
    page: 1,
    totalPages: 1,
    totalCount: 0,
    loadingOutlets: [],
    onlineCount: 0,
    offlineCount: 0,
    onlineOnCount: 0,
    onlineOffCount: 0,
    search: "",
    searchInput: "",
    dark: typeof document !== "undefined" &&
      document.body.classList.contains("dark-mode"),
  };

  componentDidMount() {
    this.ensureGroupsLoaded();
    this.fetchOutlets();
    if (typeof document !== "undefined") {
      this.darkObserver = new MutationObserver(() => {
        const isDark = document.body.classList.contains("dark-mode");
        this.setState((prev) => (prev.dark === isDark ? null : { dark: isDark }));
      });
      this.darkObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.groupsLoaded !== this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.ensureGroupsLoaded();
    }

    if (prevProps.match.params.groupId !== this.props.match.params.groupId) {
      this.fetchOutlets({ page: 1 });
    }
  }

  componentWillUnmount() {
    this.cancelRequest?.cancel?.();
    this.darkObserver && this.darkObserver.disconnect();
  }

  ensureGroupsLoaded = () => {
    const { resources = {}, fetchResource } = this.props;
    if (!resources.groupsLoaded && !resources.groupsLoading) {
      fetchResource("groups");
    }
  };

  groupId = () =>
    this.props.groupId ||
    this.props.match?.params?.groupId;

  getGroup = () => {
    if (this.props.currentGroup) return this.props.currentGroup;
    const { resources = {} } = this.props;
    return (resources.groups || []).find(
      (group) => String(group.id) === String(this.groupId())
    );
  };

  fetchOutlets = async ({ page, search } = {}) => {
    const nextPage = page || this.state.page || 1;
    const nextSearch = search ?? this.state.search;
    this.setState({ loading: true });

    this.cancelRequest?.cancel?.();
    this.cancelRequest = axios.CancelToken.source();

    try {
      const response = await axios.get("/v2/outlets", {
        params: {
          page: nextPage,
          limit: DEFAULT_PAGE_SIZE,
          groupId: this.groupId(),
          search: nextSearch,
        },
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        cancelToken: this.cancelRequest.token,
      });

      const meta = response?.data?.meta || {};

      this.setState({
        outlets: response?.data?.data || [],
        page: nextPage,
        loading: false,
        totalPages: meta.totalPages || 1,
        totalCount: meta.totalCount || 0,
        onlineCount: meta.onlineCount ?? 0,
        offlineCount: meta.offlineCount ?? 0,
        onlineOnCount: meta.onlineOnCount ?? 0,
        onlineOffCount: meta.onlineOffCount ?? 0,
        search: nextSearch,
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error(error);
        NotificationManager.alert(
          "Failed to load outlets for this group",
          "Error"
        );
        this.setState({ loading: false });
      }
    }
  };

  handleSearchSubmit = (event) => {
    event.preventDefault();
    this.fetchOutlets({ page: 1, search: this.state.searchInput });
  };

  handleRemoveOutlet = async (outletId) => {
    try {
      await axios.post(
        `/v1/groups/${this.groupId()}/removeOutlet`,
        { outletId },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      NotificationManager.success("Outlet removed from group");
      await Promise.all([
        this.fetchOutlets({ page: this.state.page }),
        this.props.fetchResource("groups"),
      ]);
    } catch (error) {
      console.error(error);
      NotificationManager.alert("Failed to remove outlet", "Error");
    }
  };

  refreshOutlet = (outletId) => {
    const refreshFn = this.props.getOutletItemAndData;
    if (typeof refreshFn !== "function") {
      this.fetchOutlets({ page: this.state.page, search: this.state.search });
      return;
    }
    refreshFn(outletId, (err) => {
      if (err) {
        console.error(err);
      }
      this.fetchOutlets({ page: this.state.page, search: this.state.search });
    });
  };

  handleUpdateOutlet = (outletId, status, cb = () => {}) => {
    const updateFn = this.props.updateOutletItem;
    if (typeof updateFn !== "function") {
      NotificationManager.alert("Outlet control is unavailable", "Error");
      return;
    }
    this.setState((prev) => ({
      loadingOutlets: [...prev.loadingOutlets, outletId],
    }));
    updateFn(outletId, status, (err, data) => {
      this.setState((prev) => ({
        loadingOutlets: prev.loadingOutlets.filter((id) => id !== outletId),
      }));
      cb?.(err, data);
      if (err) {
        console.error(err);
        return;
      }
      if (status.state === "REBOOT") {
        const rebootTime = typeof data?.rebootTime === "number" ? data.rebootTime : null;
        const delaySeconds = rebootTime !== null ? rebootTime + 1 : 5;
        setTimeout(() => this.refreshOutlet(outletId), delaySeconds * 1000);
      } else {
        this.refreshOutlet(outletId);
      }
    });
  };

  getAddOutletModalOptions = () => ({
    preventOutsideClose: true,
    modalStyle: {
      minWidth: "720px",
      width: "900px",
      maxWidth: "95vw",
      maxHeight: "85vh",
    },
    contentStyle: {
      maxHeight: "70vh",
      overflowY: "auto",
    },
  });

  openAddOutletModal = () => {
    ModalManager.confirm(
      "",
      "Add outlets to group",
      "",
      (submitted, close) => {
        if (submitted) {
          close();
        }
      },
      ({ close }) => (
        <AddOutletModal
          groupId={this.groupId()}
          existingOutletIds={new Set(this.getGroup()?.items || [])}
          onSuccess={async () => {
            await Promise.all([
              this.fetchOutlets({ page: 1 }),
              this.props.fetchResource("groups"),
            ]);
          }}
          close={close}
        />
      ),
      true,
      true,
      this.getAddOutletModalOptions()
    );
  };

  render() {
    const group = this.getGroup();
    const {
      outlets,
      loading,
      totalCount,
      page,
      totalPages,
      dark,
      onlineCount,
      offlineCount,
      onlineOnCount,
      onlineOffCount,
      loadingOutlets,
    } = this.state;

    if (!group) {
      return (
        <div style={{ padding: "2rem" }}>
          <Loading description="Loading outlets..." />
        </div>
      );
    }

    const totalOutlets = Array.isArray(group?.items) ? group.items.length : 0;
    const columnMap = {
      outletNumber: 1,
      outletName: 6,
      customField1: 4,
      customField2: 4,
      pdu: 5,
      state: 2,
      currentRms: 3,
      power: 3,
      voltage: 3,
      operation: 10,
      remove: 3,
    };
    const noOutletsWithPowerMeasurements =
      outlets.filter((o) => o.currentRms === undefined || o.currentRms === null)
        .length === outlets.length;
    const role = this.props.user.role;
    const outletsSwitchesAllowed =
      !role || role.switchAllOutletsAllowed;

    return (
      <div className="device-groups-page">
        <div className="device-groups-header">
          <div>
            <h2 className="device-groups-header__title">
              {group?.name || "Outlet Group"}
            </h2>
            <div className="device-groups-header__description">
              {group?.description ||
                "Track and manage outlets across every SynLink PDU in this group."}
            </div>
          </div>
          <div className="device-groups-header__actions">
            <button className="button" onClick={this.openAddOutletModal}>
              Add outlets
            </button>
          </div>
        </div>

        <div className="device-groups-summary">
          <SummaryCard
            label="Outlets in group"
            value={totalOutlets}
            meta="Total assigned outlets"
            accentColor="#2563eb"
          />
          <SummaryCard
            label="Online outlets"
            value={onlineCount}
            meta={
              offlineCount === 0
                ? "No offline outlets"
                : `${offlineCount} offline`
            }
            accentColor="#0ea5e9"
          />
          <SummaryCard
            label="Outlets ON"
            value={onlineOnCount}
            meta="Outlets currently online and ON"
            accentColor="#22c55e"
          />
          <SummaryCard
            label="Outlets OFF"
            value={onlineOffCount}
            meta="Outlets currently online and OFF"
            accentColor="#f97316"
          />
        </div>

        <form
          className="device-groups-filters__search"
          onSubmit={this.handleSearchSubmit}
        >
          <input
            type="text"
            placeholder="Search outlets by name, serial, or custom field"
            value={this.state.searchInput}
            onChange={(event) =>
              this.setState({ searchInput: event.target.value })
            }
          />
          <button className="button secondary" type="submit">
            Search
          </button>
        </form>

        <div
          style={{
            overflowX: "auto",
          }}
        >
          <div
            className="grid-row header hide-for-small-only"
            style={{ position: "sticky" }}
          >
            <div style={{ flex: columnMap.outletNumber }}>Outlet #</div>
            <div style={{ flex: columnMap.outletName }}>Outlet Name</div>
            <div style={{ flex: columnMap.customField1 }}>Custom Field 1</div>
            <div style={{ flex: columnMap.customField2 }}>Custom Field 2</div>
            <div style={{ flex: columnMap.pdu }}>PDU</div>
            <div style={{ flex: columnMap.state }}>State</div>
            {!noOutletsWithPowerMeasurements && (
              <>
                <div style={{ flex: columnMap.currentRms }}>Current RMS</div>
                <div style={{ flex: columnMap.power }}>Power</div>
                <div style={{ flex: columnMap.voltage }}>Voltage</div>
              </>
            )}
            <div style={{ flex: columnMap.operation }}>Operation</div>
            <div style={{ flex: columnMap.remove }}></div>
          </div>

          {loading && outlets.length === 0 && (
            <LoadingSection
              columnMap={columnMap}
              showPowerColumns={!noOutletsWithPowerMeasurements}
            />
          )}

          {!loading && outlets.length === 0 && (
            <div className="device-groups-empty">
              <div className="device-groups-empty__description">
                No outlets match the current filters.
              </div>
            </div>
          )}

          {outlets.map((outlet) => (
            <GroupOutletRow
              key={outlet.outlet_id}
              outlet={outlet}
              columnMap={columnMap}
              noOutletsWithPowerMeasurements={noOutletsWithPowerMeasurements}
              dark={dark}
              outletsSwitchesAllowed={outletsSwitchesAllowed}
              isUpdating={loadingOutlets.includes(outlet.outlet_id)}
              onUpdateOutlet={this.handleUpdateOutlet}
              onRemove={this.handleRemoveOutlet}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="device-groups-pagination" style={{ marginTop: "1.5rem" }}>
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={Math.max(0, (page || 1) - 1)}
              pageCount={totalPages}
              onPageChange={({ selected }) =>
                this.fetchOutlets({ page: selected + 1 })
              }
              marginPagesDisplayed={1}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
              disabledClassName="disabled"
            />
          </div>
        )}
        <div className="device-groups-table__meta">
          Showing {outlets.length} of {totalCount} outlets
        </div>
      </div>
    );
  }
}

const SummaryCard = ({ label, value, meta, accentColor }) => (
  <div className="device-groups-summary-card">
    <div className="device-groups-summary-card__label">{label}</div>
    <div className="device-groups-summary-card__value">{value}</div>
    <div className="device-groups-summary-card__meta">{meta}</div>
    <div
      className="device-groups-summary-card__accent"
      style={{ backgroundColor: accentColor }}
    />
  </div>
);

const formatMetric = (value, unit) =>
  typeof value === "number" ? `${value.toFixed(2)} ${unit}` : "—";

const renderCustomFieldValue = (value) =>
  value ? value : <span className="muted">Not set</span>;

const GroupOutletRow = ({
  outlet,
  columnMap,
  noOutletsWithPowerMeasurements,
  outletsSwitchesAllowed,
  onUpdateOutlet,
  onRemove,
  isUpdating,
  dark,
}) => {
  const outletNumber = outlet.pduLabeledOutletNum ?? outlet.outlet_num ?? outlet.outlet_id ?? "";
  const detailLink = `/devices/connected/${outlet.enclosureSerialNumber}/outlets/${outlet.outlet_id}`;
  const deviceLabel = outlet.deviceName || `PDU SN:${outlet.enclosureSerialNumber}`;
  const deviceLink = `/devices/connected/${outlet.enclosureSerialNumber}`;
  const connectionEstablished = outlet.connectionState === "Connected";
  const hasState = typeof outlet.state === "string" && outlet.state.trim() !== "";
  const stateValue = hasState ? outlet.state.trim().toUpperCase() : "—";
  const statusColor =
    hasState && outlet.state === "ON"
      ? "forestgreen"
      : hasState && outlet.state === "OFF"
      ? "maroon"
      : "";

  const metadataFields = [
    {
      key: "device",
      label: "PDU",
      value: (
        <Link to={deviceLink}>
          {deviceLabel}
        </Link>
      ),
    },
    {
      key: "custom1",
      label: "Custom Field 1",
      value: renderCustomFieldValue(outlet.customfield1),
      skipDefaultValueClass: true,
    },
    {
      key: "custom2",
      label: "Custom Field 2",
      value: renderCustomFieldValue(outlet.customfield2),
      skipDefaultValueClass: true,
    },
    {
      key: "state",
      label: "State",
      value: stateValue,
      valueClassName: "outlet-card__value--status",
    },
  ];

  const metricFields = noOutletsWithPowerMeasurements
    ? []
    : [
        { key: "current", label: "Current RMS", value: formatMetric(outlet.currentRms, "A") },
        { key: "power", label: "Power", value: formatMetric(outlet.power, "VA") },
        { key: "voltage", label: "Voltage", value: formatMetric(outlet.circuitVoltage, "V") },
      ];

  const operationConfigs =
    outletsSwitchesAllowed && typeof onUpdateOutlet === "function"
      ? getOperationConfigs({
          outlet,
          darkMode: dark,
          onUpdateOutlet,
        })
      : [];

  const operationContent = (() => {
    if (!outletsSwitchesAllowed) {
      return <span className="muted">No access</span>;
    }
    if (isUpdating) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Loading black />
          <span>Updating...</span>
        </div>
      );
    }
    if (operationConfigs.length === 0) {
      return <span className="muted">Unavailable</span>;
    }
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", padding: "0px" }}>
        {operationConfigs.map((config) => (
          <button
            key={`${outlet.outlet_id}-${config.key}-desktop`}
            type="button"
            className="spreadsheet-badge"
            style={{
              ...config.style,
              backgroundColor: config.backgroundColor,
              color: config.textColor || "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
            onClick={() => config.action()}
            disabled={isUpdating}
            title={config.title}
          >
            <i className="material-icons" aria-hidden="true" style={{ color: config.iconColor }}>
              {config.icon}
            </i>
            <span>{config.label}</span>
          </button>
        ))}
      </div>
    );
  })();

  const footerContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {outletsSwitchesAllowed && operationConfigs.length > 0 && (
        <div className="outlet-card__operations">
          {operationConfigs.map((config) => (
            <button
              key={`${outlet.outlet_id}-${config.key}-mobile`}
              type="button"
              className="outlet-card__operation-button"
              style={{
                backgroundColor: config.backgroundColor,
                color: config.textColor || "inherit",
              }}
              onClick={() => config.action()}
              disabled={isUpdating}
              title={config.title}
            >
              <i className="material-icons" aria-hidden="true" style={{ color: config.iconColor }}>
                {config.icon}
              </i>
              <span>{config.mobileLabel || config.label}</span>
            </button>
          ))}
        </div>
      )}
      <button
        className="button secondary small"
        type="button"
        onClick={() => onRemove(outlet.outlet_id)}
      >
        Remove from group
      </button>
    </div>
  );

  return (
    <>
      <div
        className={`grid-row-wrapper hide-for-small-only ${
          connectionEstablished ? "" : "disconnected"
        }`}
      >
        <div className="grid-row">
          <div style={{ flex: columnMap.outletNumber }}>
            <Link to={detailLink}>{outletNumber}</Link>
          </div>
          <div style={{ flex: columnMap.outletName }}>
            <Link to={detailLink}>
              <span style={{ textDecoration: "underline" }}>
                {outlet.outletName || `Outlet ${outletNumber}`}
              </span>
            </Link>
          </div>
          <div style={{ flex: columnMap.customField1 }}>
            {renderCustomFieldValue(outlet.customfield1)}
          </div>
          <div style={{ flex: columnMap.customField2 }}>
            {renderCustomFieldValue(outlet.customfield2)}
          </div>
          <div style={{ flex: columnMap.pdu }}>
            <Link to={deviceLink}>{deviceLabel}</Link>
          </div>
          <div style={{ flex: columnMap.state }}>
            <span style={{ fontWeight: 600, color: statusColor }}>{stateValue}</span>
          </div>
          {!noOutletsWithPowerMeasurements && (
            <>
              <div style={{ flex: columnMap.currentRms }}>
                {formatMetric(outlet.currentRms, "A")}
              </div>
              <div style={{ flex: columnMap.power }}>
                {formatMetric(outlet.power, "VA")}
              </div>
              <div style={{ flex: columnMap.voltage }}>
                {formatMetric(outlet.circuitVoltage, "V")}
              </div>
            </>
          )}
          <div style={{ flex: columnMap.operation }}>{operationContent}</div>
          <div style={{ flex: columnMap.remove }}>
            <button
              type="button"
              onClick={() => onRemove(outlet.outlet_id)}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
      <OutletCard
        detailLink={detailLink}
        outletNumber={outletNumber}
        outletName={outlet.outletName || `Outlet ${outletNumber}`}
        connectionEstablished={connectionEstablished}
        metadata={metadataFields}
        metrics={metricFields}
        hideMetrics={noOutletsWithPowerMeasurements}
        footerContent={footerContent}
      />
    </>
  );
};

const getOperationConfigs = ({ outlet, darkMode, onUpdateOutlet }) => {
  const configs = [];

  if (outlet.state === "OFF") {
    configs.push({
      key: "turn-on",
      label: "Turn On",
      mobileLabel: "Turn On",
      icon: "offline_bolt",
      iconColor: "forestgreen",
      backgroundColor: darkMode ? "#2e4a2e" : "rgb(194 223 194)",
      textColor: darkMode ? "#f3f3f3" : undefined,
      style: { padding: "3px 6px" },
      action: () => onUpdateOutlet(outlet.outlet_id, { state: "ON" }),
      title: "Switch outlet from OFF to ON",
    });
  }

  if (outlet.state === "ON") {
    configs.push({
      key: "turn-off",
      label: "Turn Off",
      mobileLabel: "Turn Off",
      icon: "pause_circle",
      iconColor: "maroon",
      backgroundColor: darkMode ? "#4a2e2e" : "rgb(235 210 210)",
      textColor: darkMode ? "#f3f3f3" : undefined,
      style: { padding: "3px 6px" },
      action: () => onUpdateOutlet(outlet.outlet_id, { state: "OFF" }),
      title: "Switch outlet from ON to OFF",
    });
  }

  if (outlet.state) {
    configs.push({
      key: "cycle",
      label: "Cycle",
      mobileLabel: "Cycle",
      icon: "replay",
      iconColor: "#1f2933",
      backgroundColor: darkMode ? "#e2e8f0" : "#d9e2ec",
      textColor: "#1f2933",
      style: {
        paddingLeft: "5px",
        paddingTop: "3px",
        paddingBottom: "3px",
        paddingRight: "0px",
      },
      action: () => onUpdateOutlet(outlet.outlet_id, { state: "REBOOT" }),
      title: "Reboot outlet using configured reboot time",
    });
  }

  return configs;
};

const LoadingSection = ({ columnMap, showPowerColumns }) => (
  <div>
    {[0, 1, 2, 3].map(index => (
      <div className="grid-row-wrapper" key={index}>
        <div className="grid-row">
          <div style={{ flex: columnMap.outletNumber }}>
            <div className="skeleton-blank" />
          </div>
          <div style={{ flex: columnMap.outletName }}>
            <div className="skeleton-blank" />
          </div>
          <div style={{ flex: columnMap.customField1 }}>
            <div className="skeleton-blank" />
          </div>
          <div style={{ flex: columnMap.customField2 }}>
            <div className="skeleton-blank" />
          </div>
          <div style={{ flex: columnMap.pdu }}>
            <div className="skeleton-blank" />
          </div>
          <div style={{ flex: columnMap.state }}>
            <div className="skeleton-blank" />
          </div>
          {showPowerColumns && (
            <>
              <div style={{ flex: columnMap.currentRms }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnMap.power }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnMap.voltage }}>
                <div className="skeleton-blank" />
              </div>
            </>
          )}
          <div style={{ flex: columnMap.operation }}>
            <div className="skeleton-blank" />
          </div>
          <div style={{ flex: columnMap.remove }}>
            <div className="skeleton-blank" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

class AddOutletModal extends React.Component {
  state = {
    outlets: [],
    loading: false,
    page: 1,
    totalPages: 1,
    selected: new Set(),
    search: "",
  };

  componentDidMount() {
    this.fetchAvailableOutlets();
  }

  fetchAvailableOutlets = async ({ page, search } = {}) => {
    const nextPage = page || this.state.page || 1;
    const nextSearch = search ?? this.state.search;
    this.setState({ loading: true });
    try {
      const response = await axios.get("/v2/outlets", {
        params: {
          page: nextPage,
          limit: DEFAULT_PAGE_SIZE,
          search: nextSearch,
        },
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      const rows = (response?.data?.data || []).filter(
        (outlet) =>
          !this.props.existingOutletIds.has(String(outlet.outlet_id))
      );

      this.setState({
        outlets: rows,
        page: nextPage,
        totalPages: response?.data?.meta?.totalPages || 1,
        search: nextSearch,
        loading: false,
      });
    } catch (error) {
      console.error(error);
      NotificationManager.alert("Failed to load outlets", "Error");
      this.setState({ loading: false });
    }
  };

  toggleSelection = (outletId) => {
    this.setState((prevState) => {
      const next = new Set(prevState.selected);
      if (next.has(outletId)) {
        next.delete(outletId);
      } else {
        next.add(outletId);
      }
      return { selected: next };
    });
  };

  handleSubmit = async () => {
    if (this.state.selected.size === 0) {
      NotificationManager.alert("Select at least one outlet", "Error");
      return;
    }
    try {
      await axios.post(
        `/v1/groups/${this.props.groupId}/addOutlet`,
        {
          outletIds: Array.from(this.state.selected),
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      NotificationManager.success("Outlets added");
      await this.props.onSuccess?.();
      this.props.close();
    } catch (error) {
      console.error(error);
      NotificationManager.alert("Failed to add outlets", "Error");
    }
  };

  render() {
    const { outlets, loading, page, totalPages, selected } = this.state;
    return (
      <div style={{ minWidth: "480px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <TextInput
              label="Search outlets"
              value={this.state.search}
              callback={(value) => this.setState({ search: value })}
              placeholder="Search by outlet name, serial, or custom field"
              inputContainerStyle={{ margin: "0px"}}
            />
          </div>
          <button
            className="button secondary"
            type="button"
            onClick={() =>
              this.fetchAvailableOutlets({ page: 1, search: this.state.search })
            }
          >
            Search
          </button>
        </div>
        <div className="outlet-group-modal-table">
          <div className="outlet-group-modal-table__header">
            <div />
            <div>Outlet</div>
            <div>Device</div>
            <div>Custom Field 1</div>
            <div>Custom Field 2</div>
          </div>
          {loading ? (
            <div className="outlet-group-modal-table__row">
              <Loading black />
            </div>
          ) : (
            outlets.map((outlet) => (
              <div
                key={outlet.outlet_id}
                className="outlet-group-modal-table__row"
              >
                <div>
                  <input
                    type="checkbox"
                    checked={selected.has(outlet.outlet_id)}
                    onChange={() => this.toggleSelection(outlet.outlet_id)}
                  />
                </div>
                <div>
                  <div className="outlet-group-table__title">
                    {outlet.outletName || `Outlet ${outlet.outletNumber}`}
                  </div>
                  <div className="outlet-group-table__meta">
                    Outlet {outlet.outletNumber}
                  </div>
                </div>
                <div>{outlet.deviceName || `PDU SN:${outlet.enclosureSerialNumber}`}</div>
                <div>
                  {outlet.customfield1 || <span className="muted">Not set</span>}
                </div>
                <div>
                  {outlet.customfield2 || <span className="muted">Not set</span>}
                </div>
              </div>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div className="device-groups-pagination" style={{ marginTop: "1rem" }}>
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={Math.max(0, (page || 1) - 1)}
              pageCount={totalPages}
              onPageChange={({ selected }) =>
                this.fetchAvailableOutlets({ page: selected + 1 })
              }
              marginPagesDisplayed={1}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
              disabledClassName="disabled"
            />
          </div>
        )}
        <div className="outlet-group-modal-actions">
          <button
            className="button secondary"
            type="button"
            onClick={() => this.props.close()}
          >
            Cancel
          </button>
          <button className="button" type="button" onClick={this.handleSubmit}>
            Add selected
          </button>
        </div>
      </div>
    );
  }
}

class OutletGroupSettings extends React.Component {
  state = {
    name: "",
    description: "",
    saving: false,
    deleting: false,
  };

  componentDidMount() {
    this.ensureGroupsLoaded();
    this.syncFromProps();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.groups !== this.props.resources.groups ||
      prevProps.match.params.groupId !== this.props.match.params.groupId
    ) {
      this.syncFromProps();
    }
    if (
      prevProps.resources.groupsLoaded !== this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.ensureGroupsLoaded();
    }
  }

  ensureGroupsLoaded = () => {
    const { resources = {}, fetchResource } = this.props;
    if (!resources.groupsLoaded && !resources.groupsLoading) {
      fetchResource("groups");
    }
  };

  groupId = () =>
    this.props.groupId ||
    this.props.match?.params?.groupId;
    
  getGroup = () => {
    const { resources = {} } = this.props;
    return (resources.groups || []).find(
      (group) => String(group.id) === String(this.groupId())
    );
  };

  syncFromProps = () => {
    const group = this.getGroup();
    if (group) {
      this.setState({
        name: group.name || "",
        description: group.description || "",
      });
    }
  };

  handleSave = async () => {
    const { name } = this.state;
    if (!name.trim()) {
      NotificationManager.alert("Group name is required", "Error");
      return;
    }
    this.setState({ saving: true });
    try {
      await axios.put(`/v1/groups/${this.groupId()}`, {
        name: this.state.name,
        description: this.state.description,
        groupType: "outlets",
      }, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      NotificationManager.success("Outlet group updated");
      this.props.fetchResource("groups");
    } catch (error) {
      console.error(error);
      NotificationManager.alert("Failed to update outlet group", "Error");
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async () => {
    if (!window.confirm("Delete this outlet group? This action cannot be undone.")) {
      return;
    }
    this.setState({ deleting: true });
    try {
      await axios.delete(`/v1/groups/${this.groupId()}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      NotificationManager.success("Outlet group deleted");
      this.props.fetchResource("groups");
      this.props.history.push("/devices/outlet-groups");
    } catch (error) {
      console.error(error);
      NotificationManager.alert("Failed to delete outlet group", "Error");
    } finally {
      this.setState({ deleting: false });
    }
  };

  render() {
    const group = this.getGroup();
    if (!group) {
      return (
        <div style={{ padding: "2rem" }}>
          <Loading description="Loading outlet group..." />
        </div>
      );
    }

    return (
      <div className="device-group-settings-page">
        <div className="device-group-settings-page__header">
          <div>
            <h2 className="device-group-settings-page__title">
              Outlet group settings
            </h2>
            <p className="device-group-settings-page__subtitle">
              Update the name or description of this outlet group, or delete it entirely.
            </p>
          </div>
        </div>
        <div className="device-group-settings-page__content">
          <div className="device-group-settings-card">
            <div className="device-group-settings-card__header">
              <h3>Details</h3>
              <p>Use a clear name and description to identify this outlet group.</p>
            </div>
            <div className="device-group-settings-card__body">
              <TextInput
                label="Group name"
                value={this.state.name}
                callback={(value) => this.setState({ name: value })}
              />
              <TextInput
                label="Description"
                value={this.state.description}
                callback={(value) => this.setState({ description: value })}
                placeholder="Optional"
              />
              <div>
                <button
                  className="button"
                  onClick={this.handleSave}
                  disabled={this.state.saving}
                >
                  {this.state.saving ? <Loading /> : "Save changes"}
                </button>
              </div>
            </div>
          </div>

          <div className="device-group-settings-card device-group-settings-card--danger">
            <div className="device-group-settings-card__header">
              <h3>Delete outlet group</h3>
              <p>This will permanently remove the outlet group.</p>
            </div>
            <div className="device-group-settings-card__body">
              <button
                className="device-group-settings__button device-group-settings__button--danger"
                onClick={this.handleDelete}
                disabled={this.state.deleting}
              >
                {this.state.deleting ? <Loading /> : "Delete outlet group"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const OutletGroupActivity = (props) => {
  const groupId = props.groupId || props.match?.params?.groupId;
  return (
    <LoggingUserLogs
      {...props}
      queryParams={{
        outletGroupId: groupId,
        action: "outlet_switched,outlet_edited",
      }}
    />
  );
};

export default class OutletGroupPage extends React.Component {
  componentDidMount() {
    this.ensureGroupsLoaded();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.groupsLoaded !== this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.ensureGroupsLoaded();
    }
  }

  ensureGroupsLoaded = () => {
    const { resources = {}, fetchResource } = this.props;
    if (!resources.groupsLoaded && !resources.groupsLoading) {
      fetchResource("groups");
    }
  };

  render() {
    const { resources = {} } = this.props;
    const groupId = this.props.match.params.groupId;
    const basePath = `/devices/outlet-groups/${groupId}`;
    const groups = resources.groups || [];
    const currentGroup = groups.find(
      (group) => String(group.id) === String(groupId)
    );
    const groupsLoaded = resources.groupsLoaded;
    const groupsLoading = resources.groupsLoading;

    if (!groupsLoaded) {
      return (
        <div style={{ padding: "2rem" }}>
          <Loading />
        </div>
      );
    }

    if (!currentGroup) {
      return (
        <div className="device-groups-page">
          <div className="device-groups-empty">
            <div className="device-groups-empty__title">Outlet group not found</div>
            <div className="device-groups-empty__description">
              The requested outlet group does not exist or you no longer have access.
            </div>
            <Link to="/devices/outlet-groups" className="button">
              Back to outlet groups
            </Link>
          </div>
        </div>
      );
    }

    const isAdmin =
      this.props.user?.has_admin_privileges ||
      this.props.user?.is_enterprise_admin;
    const sharedProps = { ...this.props, currentGroup };

    const sections = [
      {
        label: "Manage Outlets",
        icon: "outlet",
        path: `${basePath}/outlets`,
        component: OutletGroupOutlets,
        props: { ...sharedProps, groupId },
        exact: true,
      },
      {
        label: "Settings",
        icon: "settings",
        path: `${basePath}/settings`,
        component: OutletGroupSettings,
        props: { ...sharedProps, groupId },
        exact: true,
        disabled: !isAdmin,
      },
      {
        label: "Activity Log",
        icon: "history",
        path: `${basePath}/activity-log`,
        component: OutletGroupActivity,
        props: { ...sharedProps, groupId },
        exact: true,
      },
    ];

    const visibleSections = sections.filter((section) => !section.disabled);

    return (
      <div>
        <SubHeader sections={visibleSections} />
        <Switch>
          {sections.map((section) => (
            <Route
              key={section.path}
              path={section.path}
              exact={section.exact}
              render={(routeProps) => {
                if (section.disabled) {
                  return <Redirect to={`${basePath}/outlets`} />;
                }
                const Comp = section.component;
                return (
                  <Comp
                    {...(section.props || {})}
                    {...routeProps}
                  />
                );
              }}
            />
          ))}
          <Route
            exact
            path={`/devices/outlet-groups/:groupId`}
            render={(props) => (
              <Redirect to={`/devices/outlet-groups/${props.match.params.groupId}/outlets`} />
            )}
          />
        </Switch>
      </div>
    );
  }
}
